import type { PlatformType } from "@/types";
import { validateFacebookAdAccount } from "@/services/mediaApi/facebookValidation";
import { validateTikTokAdvertiser } from "@/services/mediaApi/tiktokValidation";
import { validateLineAdAccount } from "@/services/mediaApi/lineValidation";

/** Config for the "manual credentials" (token) connection method, per platform. */
export interface ManualMethodConfig {
  tokenLabel: string;
  tokenPlaceholder: string;
  /**
   * Optional SECOND secret field (e.g. LINE Ads needs an access key AND a secret
   * key). When set, the modal renders a second password input. Single-secret
   * platforms (Facebook/Google/TikTok) leave this undefined and are unaffected.
   */
  secretLabel?: string;
  secretPlaceholder?: string;
  /** which DB column the 2nd secret is saved to (only meaningful with secretLabel) */
  storeSecretAs?: "refresh_token";
  accountLabel: string;
  accountPlaceholder: string;
  /** which DB column the pasted token is saved to */
  storeTokenAs: "access_token" | "refresh_token";
  /** normalize the account id before validate + save */
  normalizeAccountId: (id: string) => string;
  /**
   * validate against the platform API before saving; omit = no validation.
   * `secret` is the 2nd field's value, passed only for platforms with secretLabel.
   */
  /**
   * Validate against the platform API before saving; omit = no validation.
   * `secret` is the 2nd field's value, passed only for platforms with
   * secretLabel — it stays REQUIRED-but-undefined rather than optional so the
   * translator can follow it without an optional-after-optional signature.
   */
  validate?: (
    accountId: string,
    token: string,
    secret: string | undefined,
    t: (key: string, options?: Record<string, unknown>) => string,
  ) => Promise<{ valid: boolean; accountName?: string; error?: string }>;
  /**
   * Translation KEYS, not sentences — "how to get these" steps; the form falls
   * back to oauthInstructions[platform] when omitted. Keys because this table
   * is module scope: a t() call here resolves once at import and then holds
   * that language for the whole session. Every field below is the same.
   */
  steps?: string[];
  /** honest note shown in the form when the connection can't sync yet */
  note?: string;
}

/**
 * Fallback for any platform that renders the manual/token form without an explicit
 * MANUAL_METHODS entry. Single Access Token + Account ID, no validation, stored in
 * access_token. (LINE now has its own entry below; this remains for any future
 * api_key platform added without a config.)
 */
export const DEFAULT_MANUAL: ManualMethodConfig = {
  tokenLabel: "settings.lblAccessToken",
  tokenPlaceholder: "settings.phAccessToken",
  accountLabel: "settings.lblAccountId",
  accountPlaceholder: "settings.phAccountId",
  storeTokenAs: "access_token",
  normalizeAccountId: (id) => id,
};

export const MANUAL_METHODS: Partial<Record<PlatformType, ManualMethodConfig>> = {
  facebook: {
    tokenLabel: "settings.lblAccessToken",
    tokenPlaceholder: "EAAxxxxxxxxxxxxx",
    accountLabel: "settings.lblAccountId",
    accountPlaceholder: "act_123456789",
    storeTokenAs: "access_token",
    normalizeAccountId: (id) => (id.startsWith("act_") ? id : `act_${id}`),
    validate: validateFacebookAdAccount,
    steps: [
      "settings.stepFb1",
      "settings.stepFb2",
      "settings.stepFb3",
    ],
  },
  google_ads: {
    tokenLabel: "settings.lblRefreshToken",
    tokenPlaceholder: "1//0g...",
    accountLabel: "settings.lblCustomerId",
    accountPlaceholder: "123-456-7890",
    storeTokenAs: "refresh_token",
    normalizeAccountId: (id) => id.replace(/-/g, ""),
    steps: [
      "settings.mStepGA1",
      "settings.mStepGA2",
      "settings.mStepGA3",
    ],
    note: "settings.mNoteGA",
  },
  google_analytics: {
    tokenLabel: "settings.lblRefreshToken",
    tokenPlaceholder: "1//0g...",
    accountLabel: "settings.lblPropertyId",
    accountPlaceholder: "123456789",
    storeTokenAs: "refresh_token",
    // GA4 property ids are numeric; the client re-adds the "properties/" prefix.
    normalizeAccountId: (id) => id.replace(/[^0-9]/g, ""),
    steps: [
      "settings.mStepGa1",
      "settings.mStepGa2",
      "settings.mStepGa3",
    ],
    note: "settings.mNoteGa",
  },
  tiktok: {
    tokenLabel: "settings.lblAccessToken",
    tokenPlaceholder: "settings.phTikTokToken",
    accountLabel: "settings.lblAdvertiserId",
    accountPlaceholder: "7255551290380402690",
    storeTokenAs: "access_token",
    normalizeAccountId: (id) => id.trim(),
    validate: validateTikTokAdvertiser,
    steps: [
      "settings.mStepTt1",
      "settings.mStepTt2",
      "settings.mStepTt3",
    ],
    note: "settings.mNoteTt",
  },
  line: {
    tokenLabel: "settings.lblAccessKey",
    tokenPlaceholder: "settings.phLineKey",
    secretLabel: "settings.lblSecretKey",
    secretPlaceholder: "settings.phLineSecret",
    storeSecretAs: "refresh_token",
    accountLabel: "settings.lblAdAccountId",
    accountPlaceholder: "A1234567890",
    storeTokenAs: "access_token",
    normalizeAccountId: (id) => id.trim(),
    validate: validateLineAdAccount,
    steps: [
      "settings.mStepLine1",
      "settings.stepLine2",
      "settings.mStepLine3",
    ],
    note: "settings.mNoteLine",
  },
};
